import { useCallback, useMemo, useRef, useState } from "react";
import { Input } from "@headlessui/react";
import { ArrowPathIcon, PaperClipIcon } from "@heroicons/react/24/solid";

interface DragAndDropUploadProps {
	accept: string;
	fileName?: string;
	onFileSelected: (file?: File) => void;
	label?: string;
	isLoading?: boolean;
}

const DEFAULT_FILE_NAME = "No file chosen";
const DEFAULT_LABEL = "Drag & drop file here";
const ERROR_ID = "dragdrop-error";

function parseAccept(accept: string): string[] {
	return accept
		.split(",")
		.map((a) => a.trim().toLowerCase())
		.filter(Boolean);
}

function matchesAcceptRule(file: File, rule: string): boolean {
	const name = file.name.toLowerCase();
	const type = file.type.toLowerCase();

	// check similar to ".csv"
	if (rule.startsWith(".")) return name.endsWith(rule);

	// check similar to "image/*"
	if (rule.endsWith("/*")) return type.startsWith(rule.slice(0, -1)); // keep trailing "/"

	// "application/pdf"
	return type === rule;
}

function isFileAllowed(file: File, rules: string[]): boolean {
	// Empty accept means allow anything
	if (rules.length === 0) return true;
	return rules.some((rule) => matchesAcceptRule(file, rule));
}

function buildDropzoneClass(isLoading: boolean, hasError: boolean): string {
	const base = [
		"relative rounded-md border border-dashed px-6 py-8",
		"flex flex-col items-center justify-center text-center transition",
	];

	const state = isLoading
		? "cursor-not-allowed bg-gray-100 text-gray-400 border-gray-200"
		: hasError
			? "cursor-pointer border-red-400 bg-red-50"
			: "cursor-pointer border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-gray-400";

	const focus =
		!isLoading &&
		"focus-within:ring-2 focus-within:ring-primary-600 focus-within:ring-offset-2";

	return [...base, state, focus].filter(Boolean).join(" ");
}

function buildFileNameClass(isLoading: boolean): string {
	return [
		"mt-4 w-full max-w-md rounded-md border border-gray-200 px-3 py-2 text-left text-xs",
		isLoading ? "bg-gray-100 text-gray-400" : "bg-white text-gray-700",
	].join(" ");
}

export default function DragAndDropUpload({
	accept,
	fileName = DEFAULT_FILE_NAME,
	onFileSelected,
	label = DEFAULT_LABEL,
	isLoading = false,
}: DragAndDropUploadProps) {
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [error, setError] = useState<string | null>(null);

	const acceptRules = useMemo(() => parseAccept(accept), [accept]);

	const openFilePicker = useCallback(() => {
		if (isLoading) return;
		inputRef.current?.click();
	}, [isLoading]);

	const clearNativeInputValue = useCallback(() => {
		// Allows selecting the same file again to trigger onChange.
		if (inputRef.current) inputRef.current.value = "";
	}, []);

	const reject = useCallback(
		(message: string) => {
			setError(message);
			onFileSelected(undefined);
			clearNativeInputValue();
		},
		[onFileSelected, clearNativeInputValue]
	);

	const acceptFile = useCallback(
		(file: File) => {
			setError(null);
			onFileSelected(file);
			clearNativeInputValue();
		},
		[onFileSelected, clearNativeInputValue]
	);

	const handlePick = useCallback(
		(file?: File | null) => {
			if (isLoading) return;

			// User canceled file dialog (or drop had no files)
			if (!file) {
				setError(null);
				onFileSelected(undefined);
				clearNativeInputValue();
				return;
			}

			if (!isFileAllowed(file, acceptRules)) {
				reject(`Invalid file type. Only ${acceptRules.join(", ")} allowed.`);
				return;
			}

			acceptFile(file);
		},
		[isLoading, acceptRules, onFileSelected, clearNativeInputValue, reject, acceptFile]
	);

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (isLoading) return;

			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				openFilePicker();
			}
		},
		[isLoading, openFilePicker]
	);

	const onDragOver = useCallback(
		(e: React.DragEvent) => {
			if (isLoading) return;
			e.preventDefault();
		},
		[isLoading]
	);

	const onDrop = useCallback(
		(e: React.DragEvent) => {
			if (isLoading) return;
			e.preventDefault();
			handlePick(e.dataTransfer.files?.[0]);
		},
		[isLoading, handlePick]
	);

	const dropzoneClass = useMemo(() => buildDropzoneClass(isLoading, !!error), [isLoading, error]);

	const fileNameClass = useMemo(() => buildFileNameClass(isLoading), [isLoading]);

	return (
		<div className="w-full rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
			<div
				className={dropzoneClass}
				aria-disabled={isLoading}
				aria-invalid={!!error}
				aria-describedby={error ? ERROR_ID : undefined}
				role="button"
				tabIndex={isLoading ? -1 : 0}
				onClick={openFilePicker}
				onKeyDown={onKeyDown}
				onDragOver={onDragOver}
				onDrop={onDrop}>
				{isLoading ? (
					<ArrowPathIcon width={30} className="animate-spin" />
				) : (
					<PaperClipIcon width={30} />
				)}

				<div className="mt-3">
					<div className="text-sm font-medium">{label}</div>
					<div className="mt-1 text-xs">
						<span className="mr-1">or</span>
						<span className={isLoading ? "" : "underline underline-offset-2"}>
							click to browse
						</span>
					</div>
				</div>

				<div className={fileNameClass}>{fileName || DEFAULT_FILE_NAME}</div>

				{error && (
					<div id={ERROR_ID} className="mt-2 text-xs text-red-600">
						{error}
					</div>
				)}

				<Input
					ref={inputRef}
					type="file"
					accept={accept}
					className="sr-only"
					disabled={isLoading}
					onChange={(e) => handlePick(e.target.files?.[0])}
				/>
			</div>
		</div>
	);
}
